import React, { useState, useEffect, useCallback } from 'react';
import { mediaService } from '../../services/api';
import {
  Image as ImageIcon, Video, FileText, Upload, Trash2, Copy, Check,
  Search, Folder, X, RefreshCw
} from 'lucide-react';

function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [folderFilter, setFolderFilter] = useState('all');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('site');
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await mediaService.index();
      const data = res?.data || res || [];
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load media');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const folders = [...new Set(files.map(f => f.folder))].sort();

  const filtered = files.filter(f => {
    if (filter !== 'all' && f.type !== filter) return false;
    if (folderFilter !== 'all' && f.folder !== folderFilter) return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      await mediaService.upload(file, uploadFolder);
      await loadMedia();
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.name}"? This cannot be undone.`)) return;
    try {
      await mediaService.destroy(file.path);
      setFiles(files.filter(f => f.path !== file.path));
      if (selectedFile?.path === file.path) setSelectedFile(null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Delete failed');
    }
  };

  const typeIcon = (type) => {
    if (type === 'image') return <ImageIcon size={20} />;
    if (type === 'video') return <Video size={20} />;
    return <FileText size={20} />;
  };

  const typeColor = (type) => {
    if (type === 'image') return 'text-blue-400';
    if (type === 'video') return 'text-purple-400';
    return 'text-amber-400';
  };

  const filterTabs = [
    { id: 'all', label: 'All', count: files.length },
    { id: 'image', label: 'Images', count: files.filter(f => f.type === 'image').length },
    { id: 'video', label: 'Videos', count: files.filter(f => f.type === 'video').length },
    { id: 'document', label: 'Documents', count: files.filter(f => f.type === 'document').length },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Media Library</h2>
          <p className="text-slate-400 text-sm mt-1">Upload, browse, and copy links for all your media files.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadMedia}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <div className="flex items-center gap-2">
            <select
              value={uploadFolder}
              onChange={(e) => setUploadFolder(e.target.value)}
              className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-2 text-sm text-slate-200"
            >
              <option value="site">site</option>
              <option value="page-builder">page-builder</option>
              <option value="blog">blog</option>
              <option value="products">products</option>
              <option value="sliders">sliders</option>
              <option value="team">team</option>
              <option value="misc">misc</option>
            </select>
            <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white cursor-pointer hover:bg-red-700 transition">
              <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload'}
              <input type="file" className="hidden" onChange={handleUpload} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700/40 rounded-lg px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${filter === tab.id ? 'bg-red-600/20 text-red-400 border border-red-600/30' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <select
            value={folderFilter}
            onChange={(e) => setFolderFilter(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200"
          >
            <option value="all">All Folders</option>
            {folders.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files..."
              className="bg-slate-950 border border-slate-700 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-200 w-48"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading media...</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-slate-800 rounded-xl p-12 text-center">
          <ImageIcon size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-500 text-sm">No media files found. Upload some files to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map((file) => (
            <div
              key={file.path}
              className="group relative bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-red-600/30 transition"
            >
              {/* Preview */}
              <div
                className="aspect-square flex items-center justify-center bg-slate-950 cursor-pointer"
                onClick={() => setSelectedFile(file)}
              >
                {file.type === 'image' ? (
                  <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                ) : (
                  <div className={`flex flex-col items-center gap-2 ${typeColor(file.type)}`}>
                    {typeIcon(file.type)}
                    <span className="text-[10px] uppercase font-bold">{file.extension}</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-2.5">
                <p className="text-xs text-slate-200 font-medium truncate" title={file.name}>{file.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-slate-500">{file.size_human}</span>
                  <span className="text-[10px] text-slate-600 flex items-center gap-0.5">
                    <Folder size={9} /> {file.folder}
                  </span>
                </div>
              </div>

              {/* Actions overlay */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={(e) => { e.stopPropagation(); copyUrl(file.url); }}
                  className="p-1.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 transition"
                  title="Copy URL"
                >
                  {copiedUrl === file.url ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                  className="p-1.5 rounded-lg bg-slate-800/90 hover:bg-red-600/80 text-red-400 transition"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Copied indicator */}
              {copiedUrl === file.url && (
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[10px] px-2 py-1 rounded-md font-medium">
                  URL Copied!
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedFile && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFile(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-sm font-bold text-slate-200 truncate">{selectedFile.name}</h3>
              <button onClick={() => setSelectedFile(null)} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Preview */}
              <div className="bg-slate-950 rounded-xl flex items-center justify-center min-h-[200px] max-h-[400px] overflow-hidden">
                {selectedFile.type === 'image' ? (
                  <img src={selectedFile.url} alt={selectedFile.name} className="max-w-full max-h-[400px] object-contain" />
                ) : selectedFile.type === 'video' ? (
                  <video src={selectedFile.url} controls className="max-w-full max-h-[400px]" />
                ) : (
                  <div className={`flex flex-col items-center gap-3 py-12 ${typeColor(selectedFile.type)}`}>
                    {typeIcon(selectedFile.type)}
                    <span className="text-xs uppercase font-bold">{selectedFile.extension}</span>
                    <a href={selectedFile.url} target="_blank" rel="noopener noreferrer" className="text-xs text-red-400 hover:underline">Open file</a>
                  </div>
                )}
              </div>

              {/* URL with copy button */}
              <div>
                <label className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-1 block">File URL</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={selectedFile.url}
                    className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300 font-mono"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => copyUrl(selectedFile.url)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition"
                  >
                    {copiedUrl === selectedFile.url ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-950/50 rounded-lg p-3">
                  <p className="text-slate-500 text-[10px] uppercase">Type</p>
                  <p className="text-slate-200 font-medium capitalize">{selectedFile.type}</p>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-3">
                  <p className="text-slate-500 text-[10px] uppercase">Size</p>
                  <p className="text-slate-200 font-medium">{selectedFile.size_human}</p>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-3">
                  <p className="text-slate-500 text-[10px] uppercase">Folder</p>
                  <p className="text-slate-200 font-medium">{selectedFile.folder}</p>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-3">
                  <p className="text-slate-500 text-[10px] uppercase">Modified</p>
                  <p className="text-slate-200 font-medium">{selectedFile.modified}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <a
                  href={selectedFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm font-medium hover:bg-slate-700 transition"
                >
                  Open in New Tab
                </a>
                <button
                  onClick={() => handleDelete(selectedFile)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600/10 text-red-400 text-sm font-medium hover:bg-red-600/20 transition border border-red-600/20"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaLibrary;
